import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import { useDebounce } from "use-debounce";
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/hy-am";
import { sircapApi } from "shared/api/sircap";
import { PATH_LIST } from "shared/lib/react-router";
import { getMLValue, navigateToAsset } from "helpers/functions";
import { formatMoney, getAvgValue } from "helpers/money";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import { IUserBrokerageAccount, dashboardStore } from "entities/dashboard";
import { marketStore } from "entities/market";
import { AssetType, AssetTypes } from "static/types";
import { IFavorites } from "entities/market";
import HorizontalScroll from "../components/horizontal-scroll";
import { TabBtn } from "../components/tab-btn";
import { MarketAsset } from "../components/market-asset";
import PortfolioSection from "../components/portfolio-section";
import StrategyCard from "../components/strategy-card";
import StoriesModal from "../stories";
import ResearchCard from "../components/research-card";
import BlogCard from "../components/blog-card";
import ResearchCategory from "../components/research-category";
import Filter from "../../../assets/FilterCategories.svg";
import Search from "../../../assets/SearchByCategory.svg";
// import Search from "../../../assets/SearchMarket.svg";
import "./style.scss";
import Loading from "widgets/loading";

export const DiscoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("app");
  // UI interactions
  const [story, setStory] = useState<any>(false);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const [stuck, setStuck] = useState<boolean | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [activeIndices, setActiveIndices] = useState<number[]>([]);
  // Data
  const {
    loadResearch,
    research,
    loadResearchCategories,
    researchCategories,
    loadMembers,
    members,
    isLoading,
    strategies,
    getStrategies,
    activeWtbTab,
    setActiveWtbTab,
    loadBlog,
    blog,
    loadBlogCategories,
    blogCategories,
    discover,
    discoverShowMore,
    loadDiscover,
    setDiscoverShowMore,
  } = useStore(marketStore);
  const [filteredResearch, setFilteredResearch] = useState(research);
  const [debouncedValue] = useDebounce(searchValue, 700);
  const dropdownRef = useRef<any>(null);
  useBlockScrollbar(story, [story]);

  const handleTabClick = (id: number) => {
    setActiveWtbTab(id);
    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    loadDiscover();
    getStrategies();
    loadResearchCategories();
    loadMembers();
    loadBlog([]);

    const savedTab = parseInt(localStorage.getItem("activeWtbTab") || "1");
    setActiveWtbTab(savedTab);
  }, []);

  // Handle category dropdown click outside
  const handleClickOutside = (event: MouseEvent) => {
    dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      setCategoryDropdown(false);
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setStuck(window.scrollY > 104);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle search
  useEffect(() => {
    if (searchValue && searchValue.length) {
      setSearchValue(searchValue);
    } else {
      setSearchValue("");
    }
    handleSearch();
  }, [debouncedValue]);

  useEffect(() => {
    setFilteredResearch(research);
  }, [research]);

  // Handle category filter
  useEffect(() => {
    loadResearch(activeIndices);
  }, [activeIndices]);

  const isActive = (index: number) => activeIndices.includes(index);
  const toggleActiveIndex = (index: number) => {
    setSearchValue("");
    setActiveIndices((prev) => {
      return isActive(index)
        ? prev.filter((i) => i !== index)
        : [index, ...prev];
    });
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      setFilteredResearch(research);
      return;
    }

    const searchTerm = searchValue.toLowerCase();

    const filtered = research.filter((item) => {
      const combinedTitle = Object.values(item.title).join(" ").toLowerCase();
      const combinedSubTitle = Object.values(item.subtitle)
        .join(" ")
        .toLowerCase();
      // todo
      // const combinedCategories = item.categories[item.id]
      //   .map((i) => item.categories[i].name)
      //   .join(" ")
      //   .toLowerCase();
      // ||  combinedCategories.includes(searchTerm)
      return (
        combinedTitle.includes(searchTerm) ||
        combinedSubTitle.includes(searchTerm)
      );
    });

    if (
      filtered.length !== filteredResearch.length ||
      !filtered.every((item, index) => item === filteredResearch[index])
    ) {
      setFilteredResearch(filtered);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const showDetailsPage = () => {
    const totalListsLength = discover
      .flatMap((section: any) => section.subcategories)
      .reduce(
        (total: number, subcategory: any) => total + subcategory.list.length,
        0,
      );

    return (
      <>
        {activeWtbTab === 1 ? (
          <div className="what-to-buy-main wtbm-tops">
            {discover.map((section: any, index: number) => (
              <div key={index}>
                <p className="section-title">
                  {getMLValue(section.title, i18n.language)}
                </p>

                {section.subcategories.length ? (
                  totalListsLength > 3 && !discoverShowMore[index] ? (
                    <div className="subcategory-wrapper">
                      <a
                        style={{
                          marginBlock: "0px",
                          textDecoration: "none",
                          color: section.subcategories[0].title_link
                            ? "#f8009e"
                            : "#2b2f38",
                          cursor: section.subcategories[0].title_link
                            ? "pointer"
                            : "default",
                        }}
                        href={
                          section.subcategories[0].title_link &&
                          section.subcategories[0].title_link
                        }
                      >
                        {getMLValue(
                          section.subcategories[0].title,
                          i18n.language,
                        )}
                      </a>

                      {section.subcategories[0].list
                        .slice(
                          0,
                          discoverShowMore[index]
                            ? section.subcategories.list.length
                            : 3,
                        )
                        .map((asset: any, index: number) => (
                          <MarketAsset
                            key={asset.id}
                            onClick={() => {
                              navigateToAsset(asset.type, asset.id, navigate);
                            }}
                            asset={asset}
                            about
                            onAboutClick={() =>
                              setStory({
                                asset,
                                info: section.info.find(
                                  (s: any) => s.symbol === asset.symbol,
                                ),
                              })
                            }
                          />
                        ))}
                    </div>
                  ) : (
                    section.subcategories.map((item: any, index: number) => {
                      return (
                        <div key={index} className="subcategory-wrapper">
                          <a
                            className="section-subtitle"
                            style={{
                              marginTop: index !== 0 ? "6px" : "0px ",
                              textDecoration: "none",
                              color: item.title_link ? "#f8009e" : "#2b2f38",
                              cursor: item.title_link ? "pointer" : "default",
                            }}
                            href={item.title_link && item.title_link}
                          >
                            {getMLValue(item.title, i18n.language)}
                          </a>
                          {item.list.map((asset: any, index: number) => (
                            <MarketAsset
                              key={asset.id}
                              onClick={() => {
                                navigateToAsset(asset.type, asset.id, navigate);
                              }}
                              asset={asset}
                              about
                              onAboutClick={() =>
                                setStory({
                                  asset,
                                  info: section.info.find(
                                    (s: any) => s.symbol === asset.symbol,
                                  ),
                                })
                              }
                            />
                          ))}
                        </div>
                      );
                    })
                  )
                ) : (
                  section.list
                    .slice(0, discoverShowMore[index] ? section.list.length : 3)
                    .map((asset: any) => (
                      <MarketAsset
                        key={asset.id}
                        onClick={() => {
                          navigateToAsset(asset.type, asset.id, navigate);
                        }}
                        asset={asset}
                        about
                        onAboutClick={() =>
                          setStory({
                            asset,
                            info: section.info.find(
                              (s: any) => s.symbol === asset.symbol,
                            ),
                          })
                        }
                      />
                    ))
                )}

                {(section.list.length || totalListsLength) > 3 && (
                  <button
                    onClick={() =>
                      setDiscoverShowMore(
                        discoverShowMore.map((v, i) => (i === index ? !v : v)),
                      )
                    }
                  >
                    {discoverShowMore[index] ? t("showLess") : t("showMore")}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : activeWtbTab === 2 ? (
          <div className="what-to-buy-main what-to-buy-main-research">
            <div
              ref={dropdownRef}
              className={
                stuck
                  ? "wtb-search-filter wtb-search-filter-stuck"
                  : "wtb-search-filter"
              }
            >
              <img
                onClick={(e) => {
                  categoryDropdown && setCategoryDropdown(false);
                  handleSearch();
                }}
                className="wtb-search-icon"
                src={Search}
                alt="search"
              />
              <input
                onClick={() => categoryDropdown && setCategoryDropdown(false)}
                type="text"
                placeholder={t("search")}
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
                onKeyDown={handleKeyPress}
              />

              <img
                onClick={(e) => {
                  setCategoryDropdown(!categoryDropdown);
                }}
                className="filter-categories"
                style={{
                  background: categoryDropdown
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.5)",
                }}
                src={Filter}
                alt="filter"
              />

              {categoryDropdown && (
                <div className="filter-categories-dropdown">
                  {researchCategories.map((i, index) => (
                    <ResearchCategory
                      key={index}
                      onClick={() => toggleActiveIndex(i.id)}
                      name={getMLValue(
                        researchCategories[index].name,
                        i18n.language,
                      )}
                      active={isActive(i.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {filteredResearch.map((item, index) => {
              const locales: Record<string, string> = {
                en: "en",
                ru: "ru",
                am: "hy-am",
              };
              const momentDate = moment(item.date, "YYYY.MM.DD").locale(
                locales[i18n.language] || "en",
              );
              const formattedDate = momentDate.isValid()
                ? momentDate.format("D MMMM YYYY")
                : item.date;
              return (
                <ResearchCard
                  key={index}
                  title={item.title}
                  subtitle={item.subtitle}
                  categories={item.categories}
                  onCategoryClick={(catId) => {
                    setActiveIndices([catId]);
                  }}
                  date={formattedDate}
                  image={item.background_image}
                  authors={
                    // В Strapi аналитик лежит внутри записи, у старых — только
                    // id для сопоставления с /members.
                    item.strapiAuthors ||
                    members.filter((m) => (item.authors || []).includes(m.id))
                  }
                  alias={item.alias}
                />
              );
            })}
          </div>
        ) : activeWtbTab === 3 ? (
          <div className="what-to-buy-main what-to-buy-main-blog">
            {blog.map((item, index) => {
              const momentDate = moment(item.date, "YYYY.MM.DD");
              const formattedDate = momentDate.isValid()
                ? momentDate.format("DD.MM.YYYY")
                : item.date;
              return (
                <BlogCard
                  key={index}
                  categories={item.categories}
                  // onCategoryClick={(catId) => {
                  //   setActiveIndices([catId]);
                  // }}
                  onCategoryClick={() => {}}
                  title={item.title}
                  subtitle={item.subtitle}
                  duration={item.time_to_read}
                  views={item.views}
                  date={formattedDate}
                  image={item.background_image}
                  alias={item.alias}
                />
              );
            })}
          </div>
        ) : (
          <div className="what-to-buy-main wtbm-strategy">
            {strategies.map(({ id, symbol, currency, data }, i) => (
              <StrategyCard
                id={i + 1}
                key={symbol}
                onClick={() => navigate(PATH_LIST.strategy(id))}
                asset={strategies[i]}
                minPrice={""}
                risk={data.risk}
                percent={data.annual_yield}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    if (location.state) {
      // console.log("Location state:", location.state);
      setActiveWtbTab(Number(location.state));
    }
  }, [location.state]);

  return (
    <div className="what-to-buy">
      {!isLoading && (
        <HorizontalScroll className="tab-bar">
          {discover.length > 0 && (
            <TabBtn
              text={t("selections")}
              active={activeWtbTab === 1}
              onClick={() => handleTabClick(1)}
            />
          )}
          {research.length > 0 && (
            <TabBtn
              text={t("research")}
              active={activeWtbTab === 2}
              onClick={() => handleTabClick(2)}
            />
          )}
          {blog.length > 0 && (
            <TabBtn
              text={t("blog")}
              active={activeWtbTab === 3}
              onClick={() => handleTabClick(3)}
            />
          )}
          {strategies.length > 0 && (
            <TabBtn
              text={t("strategies")}
              active={activeWtbTab === 5}
              onClick={() => handleTabClick(5)}
            />
          )}
        </HorizontalScroll>
      )}

      {isLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingBlock: 24,
          }}
        >
          <p className="assets-loading">{t("loading")}</p>
        </div>
      ) : (
        showDetailsPage()
      )}

      {story && (
        <StoriesModal
          currentStoryIndex={0}
          onAllStoriesEnd={() => setStory(false)}
          whatToBuyStories
          data={story}
        />
      )}
    </div>
  );
};
